import React, { useEffect, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { tooltipFormatterList } from 'helpers/echart-utils';
import { fetchHVACampaignTodayFunnelInbound } from 'services/hvacallingmanagment.service';
import { Col, Row } from 'react-bootstrap';
import { numberFormat } from 'helpers/utils';

// Inicializar componentes de ECharts
echarts.use([TooltipComponent, BarChart]);

// Definimos el tipo de datos que esperamos recibir
interface CampaignFunnelData {
  offered: number;
  transferred: number;
  handled: number;
  positive: number;
}

interface DescriptionMapping {
  type: string;
  bg: React.CSSProperties;
  bgdark: React.CSSProperties;
}

const baseDescriptions = ['offered', 'transferred', 'handled', 'positive'];

// Función que devuelve las opciones por defecto del gráfico
const getDefaultOptions = (
  getThemeColor: (name: string) => string,
  addAlphaToHexColor: (hex: string, alpha: number) => string,
  isDark: boolean,
  data: number[],
  offeredValue: number // Añadido para el cálculo del porcentaje
) => {
  const styles = [
    {
      // eslint-disable-next-line
      color: !isDark ? getThemeColor('success-light') : getThemeColor('success-light'),
      // eslint-disable-next-line
      emphasisColor: !isDark ? getThemeColor('success') : getThemeColor('success-dark')
    },
    {
      // eslint-disable-next-line
      color: !isDark ? getThemeColor('primary-light') : addAlphaToHexColor(getThemeColor('primary-light'),0.8),
      emphasisColor: getThemeColor('secondary')
    },
    {
      // eslint-disable-next-line
      color: !isDark ? getThemeColor('primary') : addAlphaToHexColor(getThemeColor('primary-light'),0.6),
      emphasisColor: getThemeColor('secondary')
    },
    {
      // eslint-disable-next-line
      color: !isDark ? getThemeColor('primary-dark') : addAlphaToHexColor(getThemeColor('primary-light'),0.4),
      emphasisColor: getThemeColor('secondary')
    }
  ];

  return {
    color: styles.map(style => style.color),
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      // eslint-disable-next-line
      backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
      textStyle: {
        color: !isDark ? '#202020' : '#ccc',
        fontFamily: 'Nunito Sans',
        fontSize: 14
      },
      borderColor: getThemeColor('border-color'),
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'none'
      },
      formatter: tooltipFormatterList
    },
    xAxis: {
      type: 'value',
      inverse: false,
      axisLabel: { show: false },
      show: false,
      axisLine: { lineStyle: { color: getThemeColor('tertiary-bg') } },
      axisTick: false
    },
    yAxis: {
      data: ['Positive', 'Handled', 'Transferred', 'Offered'],
      type: 'category',
      axisPointer: { type: 'none' },
      axisTick: 'none',
      splitLine: {
        interval: 1,
        lineStyle: { color: getThemeColor('secondary-bg') }
      },
      axisLine: { show: false },
      axisLabel: {
        show: false,
        align: 'left',
        margin: 80,
        color: getThemeColor('body-color')
      }
    },
    series: [
      {
        name: 'Customer conversion',
        type: 'bar',
        barWidth: '16px',
        showBackground: false,
        backgroundStyle: { borderRadius: [4, 4, 4, 4] },
        data: data.map((value, index) => ({
          value,
          itemStyle: {
            color: styles[index].color,
            borderRadius: [4, 4, 4, 4]
          },
          emphasis: {
            itemStyle: {
              color: styles[index].emphasisColor
            }
          },
          label: {
            show: true,
            position: 'insideTop',
            distance: 3,
            // eslint-disable-next-line
            formatter: offeredValue > 0 ? `${Math.round((value / offeredValue) * 100)}%` : '0%',
            fontWeight: 600,
            fontSize: 12
          }
        }))
      }
    ],
    grid: {
      right: 0,
      left: 0,
      bottom: 0,
      top: 0,
      containLabel: false
    },
    animation: true
  };
};

interface CampaignFunnelChartSmallProps {
  campaignid: number;
}

const CampaignTodayPerformanceInbound: React.FC<
  CampaignFunnelChartSmallProps
> = ({ campaignid }) => {
  // eslint-disable-next-line
  const { getThemeColor, config: { isDark }, addAlphaToHexColor } = useAppContext();
  const [chartData, setChartData] = useState<number[]>([]);
  const [offeredValue, setOfferedValue] = useState<number>(0);

  // Mapeo de descripciones a colores personalizados
  const descriptionMapping: Record<string, DescriptionMapping> = {
    offered: {
      type: 'Offered',
      bg: { backgroundColor: getThemeColor('primary-dark') },
      // eslint-disable-next-line
      bgdark: { backgroundColor: addAlphaToHexColor(getThemeColor('primary-light'),0.4) }
    },
    transferred: {
      type: 'Transferred',
      bg: { backgroundColor: getThemeColor('primary') },
      // eslint-disable-next-line
      bgdark: { backgroundColor: addAlphaToHexColor(getThemeColor('primary-light'),0.6) }
    },
    handled: {
      type: 'Handled',
      bg: { backgroundColor: getThemeColor('primary-light') },
      // eslint-disable-next-line
      bgdark: { backgroundColor: addAlphaToHexColor(getThemeColor('primary-light'),0.8) }
    },
    positive: {
      type: 'Positive',
      bg: { backgroundColor: getThemeColor('success-light') },
      bgdark: { backgroundColor: getThemeColor('success') }
    }
  };

  const [issueStats, setIssueStats] = useState(
    baseDescriptions.map(description => ({
      type: descriptionMapping[description].type,
      value: 0,
      bg: descriptionMapping[description].bg,
      bgdark: descriptionMapping[description].bgdark
    }))
  );

  // Función para obtener los datos del funnel
  const fetchData = async () => {
    try {
      // eslint-disable-next-line
      const data: CampaignFunnelData = await fetchHVACampaignTodayFunnelInbound(campaignid);
      setOfferedValue(data.offered); // Guardar el valor de offered
      setChartData([
        data.positive,
        data.handled,
        data.transferred,
        data.offered
      ]);

      // Actualizar issueStats con los datos obtenidos
      const updatedStats = baseDescriptions.map(description => ({
        type: descriptionMapping[description].type,
        value: data[description as keyof CampaignFunnelData],
        bg: descriptionMapping[description].bg,
        bgdark: descriptionMapping[description].bgdark
      }));

      setIssueStats(updatedStats);
    } catch (error) {
      console.error('Error fetching campaign data:', error);
    }
  };

  useEffect(() => {
    // Llamar a fetchData inicialmente y configurar el intervalo de actualización
    fetchData();
    const intervalId = setInterval(fetchData, 10000); // Actualizar cada 10 segundos

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(intervalId);
  }, [campaignid]);

  return (
    <>
      <Row className="mb-1">
        <h4 className="text-body-emphasis text-nowrap">Inbound Performance</h4>
      </Row>
      <Row className="g-3 mb-3">
        <Col xs={4} xl={3} xxl={2}>
          <hr className="bg-body-secondary mb-2 mt-0" />
          {issueStats.map(issue => (
            <div className="d-flex align-items-center mb-1" key={issue.type}>
              <span
                className="d-inline-block bullet-item me-2"
                style={
                  isDark
                    ? (issue.bgdark as React.CSSProperties)
                    : (issue.bg as React.CSSProperties)
                }
              />
              <p className="mb-0 fs-9 text-body lh-sm flex-1">{issue.type}</p>
              <h6 className="mb-0 text-body">{numberFormat(issue.value)}</h6>
            </div>
          ))}
        </Col>
        <Col xs={8} xl={9} xxl={10}>
          <div className="pt-2">
            <ReactEChartsCore
              echarts={echarts}
              option={getDefaultOptions(
                getThemeColor,
                addAlphaToHexColor,
                isDark,
                chartData,
                offeredValue
              )}
              style={{ height: '73px', width: '98%' }}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default CampaignTodayPerformanceInbound;
